import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth, signOut } from 'firebase/auth';

interface Palette {
  label: string,
  majorLabels: string[],
  minorLabels: string[],
}

interface SentimentCustomizerProps {
  email: string | null;
  reorder: number[];
  pcClassLabels: string[];
  pcMajClassLabels: string[];
  pcMinClassLabels: string[];
  pcClass: string[];
  emoClassLabelsMajor: string[];
  emoClassLabelsMinor: string[];
  selectedPalette: string;
  customPaletteName: string;
  defaultPalettes: Palette[];
  userPalettes: Palette[];
  allPalettes: Palette[];
  handleInputChangeMajor: (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputChangeMinor: (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  setSelectedPalette: (value: string) => void;
  setEmoClassLabelsMajor: (labels: string[]) => void;
  setEmoClassLabelsMinor: (labels: string[]) => void;
  updatePalettes: (email: string, label: string, majorLabels: string[], minorLabels: string[]) => void;
  deletePalette: (email: string, label: string) => void;
  setCustomPaletteName: (name: string) => void;
  hasChanges: () => boolean;
  navigate: (path: string) => void;
  signOut: (auth: Auth) => void;
  auth: Auth;
}

const SentimentCustomizer = forwardRef<{ openPopup: () => void }, SentimentCustomizerProps>(({
  email,
  reorder,
  pcClassLabels,
  pcMajClassLabels,
  pcMinClassLabels,
  pcClass,
  emoClassLabelsMajor,
  emoClassLabelsMinor,
  selectedPalette,
  customPaletteName,
  defaultPalettes,
  userPalettes,
  allPalettes,
  handleInputChangeMajor,
  handleInputChangeMinor,
  handleInputFocus,
  handleInputBlur,
  setSelectedPalette,
  setEmoClassLabelsMajor,
  setEmoClassLabelsMinor,
  updatePalettes,
  deletePalette,
  setCustomPaletteName,
  hasChanges,
  navigate,
  signOut,
  auth
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Expose openPopup function to parent components via ref
  useImperativeHandle(ref, () => ({
    openPopup: () => {
      setIsOpen(true);
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    }
  }), []);

  // Convert circle of fifths pitch classes back to chromatic pitch classes
  const fifthsToChromatic = (index: number, rootNote: number = 0): number => {
    // The multiplicative inverse of 7 modulo 12 is 7 itself (7*7 = 49 ≡ 1 (mod 12))
    return (index * 7) % 12;
  };

  // Create a chromatic order of indices
  const chromaticOrder = Array.from({ length: 12 }, (_, i) => i)
    .sort((a, b) => fifthsToChromatic(a) - fifthsToChromatic(b));

  const closePopup = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.height = '100%';
  };

  return (
    <>

      {isOpen && (
        <div ref={contentRef} style={{
          position: 'fixed',
          top: '3dvw',
          left: '16dvw',
          right: '23dvw',
          height: '33dvw',
          backgroundColor: '#1a1a1a',
          padding: '2dvw',
          color: '#fff',
          border: 'groove',
          borderRadius: '2dvw',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <span style={{ fontSize: '1.7dvw', fontWeight: '700', color: 'rgb(220, 220, 220)' }}>Customize Sentiments | </span>
              <span className='title-text' style={{ fontSize: '1.9dvw', fontWeight: '700' }}>Harmona </span>
              <span className='title-text' style={{ fontSize: '0.7dvw' }}> by Sentisonics</span>
            </div>
            <span onClick={closePopup} title='Close Sentiment Customizer' style={{
              textAlign: 'center', border: 'groove', verticalAlign: '-0.0dvw', cursor: 'pointer', borderRadius: '0.4dvw', padding: '0.1dvw', backgroundColor: 'black', color: 'rgb(220,220,220)', fontSize: '1dvw'
            }}>
              {'\u00a0Close\u00a0\u2716'}
            </span>
          </div>

          {/* Customize Emotion/Feeling Below */}
          <div id="emo-inputs">
            <div style={{ position: 'absolute', top: '0px', left: '0px' }}>
              <p><br></br></p><div className="inputs">

                <table style={{ top: '0dvw', position: 'absolute', fontFamily: 'Arial Unicode MS, Bravura Text', fontSize: '1.1dvw', padding: '0px', margin: '0px' }}>
                  <tbody>
                    <tr>
                      <th></th>
                      <th style={{ fontWeight: '600', textAlign: 'left' }}>M A J O R</th>
                      <th></th>
                      <th style={{ fontWeight: '600', textAlign: 'left' }}>M I N O R</th>
                    </tr>
                    {chromaticOrder.map(circleOfFifthsIndex => {
                      const chromaticIndex = fifthsToChromatic(circleOfFifthsIndex);
                      const index = reorder[circleOfFifthsIndex];
                      const titleMaj = pcMajClassLabels[index];
                      const titleMin = pcMinClassLabels[index];
                      const tableClassName = pcClass[index];
                      return (
                        <tr key={circleOfFifthsIndex}>
                          <th className={tableClassName} style={{ textAlign: 'right', paddingRight: '0.3dvw', paddingLeft: '0.1dvw', color: 'black' }}>{titleMaj}</th>
                          <td style={{ fontFamily: 'Bravura Text', fontWeight: 100, padding: '0px', margin: '0px' }}>
                            <input
                              type="text"
                              value={emoClassLabelsMajor[index]}
                              onChange={handleInputChangeMajor(index)}
                              onFocus={handleInputFocus}
                              onBlur={handleInputBlur}
                              style={{ color: "rgb(30, 30, 30)", backgroundColor: "rgb(245, 235, 190)", border: '0px', paddingTop: '0.7dvw', paddingBottom: '0.7dvw', margin: '0px' }}
                            />
                          </td>
                          <th className={tableClassName} style={{ textAlign: 'right', paddingRight: '0.3dvw', paddingLeft: '0.1dvw', color: 'black' }}>{titleMin}</th>
                          <td style={{ fontFamily: 'Bravura Text', fontWeight: 100, padding: '0px', margin: '0px' }}>
                            <input
                              type="text"
                              value={emoClassLabelsMinor[index]}
                              onChange={handleInputChangeMinor(index)}
                              onFocus={handleInputFocus}
                              onBlur={handleInputBlur}
                              style={{ color: "rgb(170, 170, 170)", backgroundColor: "rgb(70, 70, 120)", border: '0px', paddingTop: '0.7dvw', paddingBottom: '0.7dvw', margin: '0px' }}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div id='emo-selects'>
                <div>


                  <p className="title-text">Sentiment Palette</p>
                  <br></br>

                  {/* // NEW ADDITION ACRYLICODE */}
                  {/* <select style={{ width: '12vw', fontSize: '1.0vw' }}
                  value={selectedSong.songTitle}
                  onChange={event => {
                    const selectedSong = allSongs.find(({ songTitle }) => songTitle === event.target.value)
                    if (!selectedSong) return
                    setSelectedSong(selectedSong)
                    // SET ALL THE STATE VARIABLES HERE
                    setSongTitle({ title: selectedSong.songTitle, isEditable: false })
                  }}
                >
                  {/* {allPalettes.map(({ label }) => <option value={label}>{label}</option>)} */}

                  {/* {allSongs.map(({ songTitle }) => (<option key={songTitle} value={songTitle}>{songTitle}</option>))}
                  <hr />
                </select> */}

                  <select style={{ width: '12dvw', fontSize: '0.9dvw', position: 'relative', top: '0.0dvw' }}
                    value={selectedPalette}
                    onChange={event => {
                      setSelectedPalette(event.target.value)
                      const selectedPalette = allPalettes.find(({ label }) => label === event.target.value)
                      if (!selectedPalette) return
                      setEmoClassLabelsMajor(selectedPalette.majorLabels)
                      setEmoClassLabelsMinor(selectedPalette.minorLabels)
                    }}
                  >
                    {/* {allPalettes.map(({ label }) => <option value={label}>{label}</option>)} */}

                    {defaultPalettes.map(({ label }) => (<option key={label} value={label}>{label}</option>))}
                    <optgroup label="Custom Palettes">
                      {userPalettes.map(({ label }) => (<option key={label} value={label}>{label}</option>))}
                    </optgroup>

                  </select>
                  {'\u0020\u0020'}
                  <button style={{ width: "12dvw", position: 'relative', top: '0.0dvw' }}
                    className={defaultPalettes.map(p => p.label).includes(selectedPalette) ? 'button-disabled' : ''}
                    disabled={defaultPalettes.map(p => p.label).includes(selectedPalette)}
                    onClick={() => {
                      deletePalette(email!, selectedPalette);
                      setSelectedPalette(allPalettes[0].label);
                      setEmoClassLabelsMajor(allPalettes[0].majorLabels);
                      setEmoClassLabelsMinor(allPalettes[0].minorLabels);
                    }}>Delete Palette<span style={{ fontSize: '0.8dvw' }}>{' \u232B'}</span></button>

                </div>

                <button style={{ width: "12dvw", position: 'relative', top: '0.0dvw' }}
                  className={defaultPalettes.map(p => p.label).includes(selectedPalette) || hasChanges() ? 'button-disabled' : ''}
                  disabled={defaultPalettes.map(p => p.label).includes(selectedPalette) || hasChanges()}
                  onClick={() => {
                    updatePalettes(email!, selectedPalette, emoClassLabelsMajor, emoClassLabelsMinor)
                  }}>Save <span style={{ fontSize: '0.9dvw' }}>{'\u2713'}</span></button>
                {'\u0020'}
                <button style={{ width: "12dvw", position: 'relative', top: '0.0dvw' }}
                  className={hasChanges() ? 'button-disabled' : ''}
                  disabled={hasChanges()}
                  onClick={() => {
                    const currentPalette = allPalettes.find(({ label }) => label === selectedPalette)
                    if (!currentPalette) return
                    setEmoClassLabelsMajor(currentPalette.majorLabels);
                    setEmoClassLabelsMinor(currentPalette.minorLabels);
                  }}>Discard Changes <span style={{ fontSize: '1.0dvw' }}>{'\u21Ba'}</span></button>
                <div>
                  <button style={{ width: "12dvw", position: 'relative', top: '0.0dvw' }}
                    className={customPaletteName === '' || allPalettes.map(p => p.label).includes(customPaletteName) ? 'button-disabled' : ''}
                    disabled={customPaletteName === '' || allPalettes.map(p => p.label).includes(customPaletteName)}
                    onClick={() => {
                      setSelectedPalette(customPaletteName)
                      setEmoClassLabelsMajor(emoClassLabelsMajor)
                      setEmoClassLabelsMinor(emoClassLabelsMinor)
                      updatePalettes(email!, customPaletteName, emoClassLabelsMajor, emoClassLabelsMinor)
                      setCustomPaletteName('')
                    }}>Save As...</button>
                  {'\u0020'}
                  <input style={{
                    width: '11.5dvw', height: '1.6dvw', fontSize: '1.0dvw', textIndent: '5px',
                    color: "rgb(250, 250, 250)", backgroundColor: "rgb(0, 0, 0)", fontStyle: "normal"
                  }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    type="text" placeholder='New Palette Name'
                    value={customPaletteName}
                    onChange={(event) => setCustomPaletteName(event.target.value)}></input>
                </div>
                {!email && <button onClick={() => navigate('/login')}>Login</button>}
                {email && <button style={{ width: "12dvw", position: 'relative', top: '0.0dvw' }} onClick={() => signOut(auth)}>Logout</button>}

              </div>
            </div>
          </div>
          {/* Customize Emotion/Feeling End */}
        </div>
      )}

      {isOpen && (
        <div onClick={closePopup} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          zIndex: 999
        }} />
      )}
    </>
  );
});

SentimentCustomizer.displayName = 'SentimentCustomizer';

export default SentimentCustomizer;